import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import {
  setQuillEditors,
  setShowAiAssistant,
  updateUseSeparateTextFields
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import CreatorQuillEditor from './creatorQuillEditor';
import { ALL_PLATFORM_ICONS, PLATFORM_COLORS } from '../../../../config/icons';
import { useState } from 'react';
import EmojiPicker from '../../../communication/messages/emojiPicker/empjiPicker';
import {
  QUILL_API_ACTION_ADD_EMOJI,
  countAllCharacters,
  fetchLinkPreview,
  getTextFromQuill,
  processLinksInQuill,
  shortenLink
} from './config';
import {
  ALL,
  MAIN_COLOR_CODES,
  PLATFORM_LIST,
  PRIMARY,
  PRIMARY_DARK
} from '../../../../config/constants';
import { getPostLimit, postExcededTextLimit } from '../../utils/postHelpers';
import CreatorTitle from '../commonElements/creatorTitle';
import CreatorCard from '../commonElements/creatorCard';
import CreatorSwitchButton from '../commonElements/creatorSwitchButton';
import CreatorPlatformTitle from '../commonElements/creatorPlatformTitle';
import toast from 'react-hot-toast';
import { setTextBlockModalUi } from '../../../../redux/slices/textBlocks';

import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';
import { lomavisShowToastr } from '@/utils/ui/toastr';

import { LomavisOverlayToolTip1 } from '@/lomavisSharedUi/atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';

export const CreatorTextEditor = () => {
  const {
    platformEnabled,
    useSeparateTextFields,
    postData,
    postTypeSelected,
    quillEditors,
    lomavisCreatorConfig
  } = useSelector((state: RootState) => state.lomavisCreator);
  const dispatch = useDispatch();
  const { t } = useTranslate();

  // ----

  // ---

  const [loadingURLs, setLoadingURLs] = useState(false);
  const isPlatformEnabled = (platform) => {
    if (platform == ALL) {
      return true;
    }
    return platformEnabled[platform];
  };

  return (
    <CreatorCard>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <CreatorTitle title="text" />
        <div className="d-flex align-items-center">
          <CreatorSwitchButton
            tooltipText={t('lomavisCreator.textEditor.switch_tooltip')}
            isOn={useSeparateTextFields}
            OnText={t('lomavisCreator.textEditor.individual')}
            onClick={() => {
              // get all values from quilleditors into list
              const quillEditorsTexts = Object.keys(quillEditors)
                .map((platform) => {
                  if (platform == ALL) {
                    return null;
                  }
                  return getTextFromQuill(quillEditors[platform], true).trim();
                })
                .filter((text) => text);
              const allEqual = (arr) => arr.every((v) => v === arr[0]);
              // check if this a disable operation
              if (useSeparateTextFields) {
                // if all texts are equal then set all quill editors to the same text
                if (
                  allEqual(quillEditorsTexts) ||
                  quillEditorsTexts.length == 0
                ) {
                  dispatch(updateUseSeparateTextFields(!useSeparateTextFields));
                } else {
                  // add toast message
                  toast.error(t('lomavisCreator.cannot_unify_diffrent_text'), {
                    duration: 5000,
                    position: 'top-right'
                  });
                }
              } else {
                dispatch(updateUseSeparateTextFields(!useSeparateTextFields));
              }
            }}
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-end">
        {[
          ALL,
          ...Object.keys(platformEnabled).filter(
            (platform) => platformEnabled[platform]
          )
        ].map((platform, index) => {
          const addEmoji = (emoji) => {
            const quillEditor = quillEditors[platform];
            quillEditor.insertText(
              quillEditor.getSelection(true).index,
              emoji,
              QUILL_API_ACTION_ADD_EMOJI
            );
          };
          return (
            <div
              key={platform}
              className={` w-100 d-flex card mb-4 ${
                platform === ALL && useSeparateTextFields ? 'd-none' : ''
              } ${platform !== ALL && !useSeparateTextFields ? 'd-none' : ''}`}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <CreatorPlatformTitle platform={platform} />
                {lomavisCreatorConfig.features.ai_enabled && (
                  <span
                    className="cursor-pointer"
                    style={{
                      background: 'linear-gradient(90deg, #2575fc , #6a11cb)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 'bold' // Optional: make it bold
                    }}
                    onClick={() => dispatch(setShowAiAssistant(true))}
                  >
                    {t('lomavisCreator.aiAssistant.open_ai_assistant')}
                  </span>
                )}
              </div>

              <div>
                <CreatorQuillEditor
                  platform={platform}
                  enabled={isPlatformEnabled(platform)}
                  quillEditors={quillEditors}
                  setQuillEditors={setQuillEditors}
                />
              </div>
              <div className="d-flex flex-column justify-content-between mt-2">
                {quillEditors?.[platform] && (
                  <div className="d-flex align-items-center justify-content-between w-100 gap-1">
                    <div className="d-flex align-items-center gap-2">
                      {' '}
                      <EmojiPicker addEmoji={addEmoji} />
                      {loadingURLs ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <LomavisOverlayToolTip1
                          text={t('lomavisCreator.URLShortener.tooltip')}
                          show={1500}
                        >
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              // Hide any active tooltips by clicking outside
                              document.body.click();
                              setLoadingURLs(true);
                              return processLinksInQuill(
                                quillEditors[platform],
                                shortenLink,
                                () => setLoadingURLs(false)
                              );
                            }}
                          >
                            <i className="fa-regular fa-link text-primary cursor-pointer"></i>
                          </span>
                        </LomavisOverlayToolTip1>
                      )}
                    </div>

                    <button
                      className="btn btn-white btn-sm pt-1 px-2"
                      onClick={() => {
                        dispatch(
                          setTextBlockModalUi({
                            attribute: 'platform',
                            value: platform
                          })
                        );
                        dispatch(
                          setTextBlockModalUi({
                            attribute: 'toggle',
                            value: true
                          })
                        );
                      }}
                    >
                      <span>
                        <i className="fa-duotone fa-photo-film"></i>
                      </span>
                      <span className="text-muted fw-bolder">
                        {t('lomavisCreator.textEditor.textBlockOpener')}
                      </span>
                    </button>
                  </div>
                )}

                {quillEditors?.[platform] && (
                  <div className="d-flex flex-wrap px-2 justify-content-end mt-2">
                    {platform !== ALL && (
                      <PlatformCharacterLimit
                        platform={platform}
                        postType={postTypeSelected[platform]}
                        charactersCount={postData[platform]?.text_length}
                      />
                    )}
                    {platform == ALL &&
                      PLATFORM_LIST.map((platform) => {
                        if (!isPlatformEnabled(platform)) return null;
                        return (
                          <PlatformCharacterLimit
                            key={platform}
                            platform={platform}
                            postType={postTypeSelected[platform]}
                            charactersCount={postData[platform]?.text_length}
                          />
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </CreatorCard>
  );
};

export const PlatformCharacterLimit: React.FC<{
  platform: string;
  postType: string;
  charactersCount: number;
}> = ({ platform, postType, charactersCount }) => {
  const { t } = useTranslate();
  // only show component if 25% of the limit is reached
  if (charactersCount < getPostLimit(platform, postType) * 0.25) {
    return null;
  }
  return (
    <div className="me-2 facebook-collapse collapse show mb-1">
      <span className="d-flex align-items-center justify-content-center">
        <i
          className={`${ALL_PLATFORM_ICONS[platform]} me-2 fs-1x small`}
          style={{ fontSize: 20, color: PLATFORM_COLORS[platform] }}
        ></i>
        <span
          className={`${
            postExcededTextLimit(platform, postType, charactersCount)
              ? 'text-danger'
              : ''
          }`}
        >
          {charactersCount} / {getPostLimit(platform, postType)}
        </span>
      </span>
    </div>
  );
};
